import { supabase } from './authService';
import { PointLog } from '../types';

export const pointService = {
  async getPointLogs(userId: string): Promise<PointLog[]> {
    const { data, error } = await supabase
      .from('point_logs')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching point logs:', error);
      return [];
    }

    return data || [];
  },

  async getUserPoints(userId: string): Promise<number> {
    const { data, error } = await supabase
      .from('user')
      .select('points')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Error fetching user points:', error);
      return 0;
    }

    return data?.points || 0;
  },

  async checkAttendance(userId: string): Promise<{ attended: boolean, streak: number }> {
    // 한국 시간(KST) 기준으로 날짜 계산
    const kstNow = new Date(new Date().toLocaleString("en-US", {timeZone: "Asia/Seoul"}));
    
    // Intl.DateTimeFormat을 사용하여 KST 기준 오늘 날짜(YYYY-MM-DD)를 정확히 추출
    const todayKst = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Seoul',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(kstNow);
    
    // KST 00:00:00 ~ 23:59:59를 UTC로 변환하여 쿼리
    const startOfKstDay = new Date(`${todayKst}T00:00:00+09:00`).toISOString();
    const endOfKstDay = new Date(`${todayKst}T23:59:59+09:00`).toISOString();

    // 오늘 출석 기록 확인 (정확히 'Daily Attendance' 사유로 조회)
    const { data: todayLogs, error: todayError } = await supabase
      .from('point_logs')
      .select('id, amount, reason, created_at')
      .eq('user_id', userId)
      .eq('reason', 'Daily Attendance')
      .gte('created_at', startOfKstDay)
      .lte('created_at', endOfKstDay);

    if (todayError) {
      console.error('Error checking attendance:', todayError);
      return { attended: false, streak: 0 };
    }

    const attended = todayLogs && todayLogs.length > 0;

    // 전체 출석 횟수(Daily Attendance 사유)를 조회하여 연속 출석 대신 누적 출석 기반으로 처리
    const { count, error: countError } = await supabase
      .from('point_logs')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('reason', 'Daily Attendance');

    if (countError) {
      console.error('Error counting attendance:', countError);
      return { attended, streak: 0 };
    }

    const totalAttendance = count || 0;

    return { attended, streak: totalAttendance };
  },

  async recordAttendance(userId: string): Promise<{ success: boolean, pointsEarned: number, streak: number }> {
    const { attended, streak: currentStreak } = await this.checkAttendance(userId);
    if (attended) return { success: false, pointsEarned: 0, streak: currentStreak };

    const newStreak = currentStreak + 1;
    let points = 10; // 기본 출석 포인트
    let bonus = 0;

    // 7일 누적 시 보상 (7, 14, 21...)
    if (newStreak > 0 && newStreak % 7 === 0) {
      bonus = 50;
    }

    const totalPoints = points + bonus;

    // 1. 포인트 로그 추가 (기본 출석)
    const { error: logError } = await supabase.from('point_logs').insert([
      { user_id: userId, amount: points, reason: 'Daily Attendance' }
    ]);

    if (logError) {
      console.error('Error logging attendance:', logError);
      return { success: false, pointsEarned: 0, streak: currentStreak };
    }

    // 2. 7일 보너스 로그 추가
    if (bonus > 0) {
      await supabase.from('point_logs').insert([
        { user_id: userId, amount: bonus, reason: '7-Day Attendance Bonus' }
      ]);
    }

    // 3. 유저 포인트 업데이트 (원자적 업데이트를 위해 RPC 사용 권장되나 현재 스키마에 따라 기존 방식 유지)
    const currentPoints = await this.getUserPoints(userId);
    const { error: updateError } = await supabase
      .from('user')
      .update({ points: currentPoints + totalPoints })
      .eq('id', userId);

    if (updateError) {
      console.error('Error updating user points:', updateError);
      // 로그는 이미 남았으므로 포인트만 업데이트 실패한 상황
      return { success: false, pointsEarned: 0, streak: currentStreak };
    }

    return { success: true, pointsEarned: totalPoints, streak: newStreak };
  },

  async deductPoints(userId: string, amount: number, reason: string): Promise<{ success: boolean, remainingPoints: number, error?: string }> {
    const currentPoints = await this.getUserPoints(userId);
    
    if (currentPoints < amount) {
      return { success: false, remainingPoints: currentPoints, error: 'Insufficient points' };
    }

    const newPoints = currentPoints - amount;

    // 포인트 로그 추가
    await supabase.from('point_logs').insert([
      { user_id: userId, amount: -amount, reason: reason }
    ]);

    // 유저 포인트 업데이트
    const { error } = await supabase
      .from('user')
      .update({ points: newPoints })
      .eq('id', userId);

    if (error) {
      console.error('Error deducting points:', error);
      return { success: false, remainingPoints: currentPoints, error: 'Database error' };
    }

    return { success: true, remainingPoints: newPoints };
  },

  async awardPoints(userId: string, amount: number, reason: string): Promise<{ success: boolean, newTotal: number, pointsEarned: number }> {
    const currentPoints = await this.getUserPoints(userId);
    const newTotal = currentPoints + amount;

    // 포인트 로그 추가
    await supabase.from('point_logs').insert([
      { user_id: userId, amount: amount, reason: reason }
    ]);

    // 유저 포인트 업데이트
    const { error } = await supabase
      .from('user')
      .update({ points: newTotal })
      .eq('id', userId);

    if (error) {
      console.error('Error awarding points:', error);
      return { success: false, newTotal: currentPoints, pointsEarned: 0 };
    }

    return { success: true, newTotal, pointsEarned: amount };
  },

  async awardPointsWithLimit(userId: string, amount: number, reason: string, dailyLimit: number): Promise<{ success: boolean, newTotal: number, pointsEarned: number, limitReached?: boolean }> {
    // 한국 시간(KST) 기준으로 날짜 계산
    const kstNow = new Date(new Date().toLocaleString("en-US", {timeZone: "Asia/Seoul"}));
    const today = kstNow.toISOString().split('T')[0];
    
    const startOfKstDay = new Date(`${today}T00:00:00+09:00`).toISOString();
    const endOfKstDay = new Date(`${today}T23:59:59+09:00`).toISOString();
    
    // 금일 해당 사유로 적립된 포인트 합계 확인
    const { data: logs, error: logsError } = await supabase
      .from('point_logs')
      .select('amount')
      .eq('user_id', userId)
      .eq('reason', reason)
      .gte('created_at', startOfKstDay)
      .lte('created_at', endOfKstDay);

    if (logsError) {
      console.error('Error checking daily point limit:', logsError);
      return { success: false, newTotal: 0, pointsEarned: 0 };
    }

    const currentDailyTotal = (logs || []).reduce((sum, log) => sum + (log.amount > 0 ? log.amount : 0), 0);
    
    if (currentDailyTotal >= dailyLimit) {
      const currentPoints = await this.getUserPoints(userId);
      return { success: false, newTotal: currentPoints, pointsEarned: 0, limitReached: true };
    }

    // 지급할 포인트가 한도를 초과하지 않도록 조정
    const actualAmount = Math.min(amount, dailyLimit - currentDailyTotal);
    
    return this.awardPoints(userId, actualAmount, reason);
  }
};
